import React from "react";
import { BytesUnit } from "@sscs/types";
import { VerticalDivider } from "@sscs/ui";
import { SquareIcon as MUISquareIcon } from "@sscs/ui/icons";
import {
  calculateFractionDigitsForUsage,
  humanReadableBytes,
} from "@sscs/utils";
import BarChart from "./BarChart";
import styles from "./capacityBar.module.scss";

export type Usage = {
  name: string;
  used: number;
  color: string;
};

type CapacityBarProps = {
  usages: Usage[];
  totalCapacity: number;
  showLegend?: boolean;
  unit?: BytesUnit;
};

export const CapacityBar = ({
  usages,
  totalCapacity,
  showLegend = true,
  unit,
}: CapacityBarProps): React.JSX.Element => {
  const totalUsage = usages.reduce((acc, usage) => acc + usage.used, 0);

  const fractionDigits = unit
    ? calculateFractionDigitsForUsage(totalUsage, totalCapacity, 2, unit)
    : 0;

  const readableUsageValue = unit
    ? humanReadableBytes(totalUsage, fractionDigits, unit, false)
    : totalUsage.toLocaleString();
  const readableTotalValue = unit
    ? humanReadableBytes(totalCapacity, fractionDigits, unit)
    : totalCapacity.toLocaleString();

  const chartData = usages.map((usage) => {
    return {
      ...usage,
      formattedUsage: unit
        ? humanReadableBytes(usage.used, fractionDigits, unit)
        : usage.used.toLocaleString(),
    };
  });

  return (
    <BarChart data={chartData} total={totalCapacity}>
      <div className={styles["usage-info"]}>
        {showLegend && (
          <>
            <div className={styles["legend"]}>
              {usages.map((usage) => (
                <div key={usage.name} className={styles["legend-item"]}>
                  {/* per-series color is dynamic data → carried by the wrapper,
                      inherited by the icon via currentColor */}
                  <span style={{ color: usage.color }}>
                    <MUISquareIcon className={styles["legend-item-icon"]} />
                  </span>
                  <div>{usage.name}</div>
                </div>
              ))}
            </div>
            <VerticalDivider />
          </>
        )}
        <div>
          <b>
            {readableUsageValue} / {readableTotalValue}
          </b>{" "}
          Used
        </div>
      </div>
    </BarChart>
  );
};

export default CapacityBar;
